import projectTemplatesService from '../services/projectTemplatesService';
import dataModel from '../models/DataModel';
import OfflineQueue from './queue/OfflineQueue';
import StoreItemProcessing from '../helpers/StoreFunctions/StoreItemProcessing';
import Metadata from '../helpers/metadata/Metadata';
import ProjectTypesCollection from '../dao/collections/ProjectTypesCollection';
import DAO from './DAO.js';
import TextMap from '../helpers/TextMap';

export const project_template_root = {
  project_template: {
    project_type: {},
    builds: {},
    markets: {},
    reference_documents: {},
  },
};

export const project_template_with_builds_tasks_root = {
  project_template: {
    project_type: {},
    builds: { tasks: { completion_criteria: { attachments: {} } } },
    markets: {},
  },
};

export const attributes_project_template_root = {
  project_template: {},
};

export const project_type_root = {
  project_type: {

    // Children are defined in this manner
    project_templates: {
      markets: { },
    },
  },
};

export default class ProjectTemplatesDAO extends DAO {

  /*************************************************************
   *
   *  Initialization
   *
   *************************************************************/

  constructor(store) {
    super(store, 'project_template');
    this.OfflineQueue = OfflineQueue;

    this.unsetProjectTemplate = this.unsetProjectTemplate.bind(this);
    this.clearProjectTemplates = this.clearProjectTemplates.bind(this);
    this.mapToReturn = this.mapToReturn.bind(this);
    this.textMap = new TextMap();
  }

  unsetProjectTemplate(projectTemplateId) {
    StoreItemProcessing.deleteItemInStore(this.itemType, projectTemplateId);
  }

  clearProjectTemplates() {
    StoreItemProcessing.clearItemsInStore(this.itemType);
  }

  mapToReturn(projectTemplate, options) {
    let denormalizedProjectTemplate = super.mapToReturn(projectTemplate, options);

    //what if we don't have builds here?
    if (denormalizedProjectTemplate && denormalizedProjectTemplate.builds) {
      let rootBuildWithHierarchy = denormalizedProjectTemplate.builds.filter(build => {
        return build && build.ancestry && build.ancestry.split('/').length == 1;
      });
      const rootBuild = { id: denormalizedProjectTemplate.root_build_id, name: denormalizedProjectTemplate.name, builds: rootBuildWithHierarchy };
      denormalizedProjectTemplate.root_build = rootBuild;
    }
    
    return denormalizedProjectTemplate;
  }

  overrideMapToReturn(response){
    if (response.included) {
      let root_build_id = null;
      response.included = response.included.filter(include => {
        if (include.type == 'build' && include.attributes.ancestry == null) {
          root_build_id = +include.id;
          return false;
        }
        else {
          return true;
        }
      });
      if (root_build_id && response.data.relationships.builds){
        response.data.relationships.builds.data = response.data.relationships.builds.data.filter(build => {
          return +build.id != root_build_id;
        });
      }
    }
    return response;
  }

  /**
   * @param  {number} projectTemplateId - project template record identifier
   * @param  {object <boolean>} options.force - forces the get to happen
   * @param  {object <object>} options.recordDescriptor - used to determine completeness of store object and to denormalize
   *                                                      returned project template
   * 
   * @returns {object<Promise<projectTemplate>>} projectTemplate - denormalized project template
   */
  getProjectTemplate(projectTemplateId, options, noPreProcess, prefetchCallback) {
    return this._getItem(projectTemplateId,
      projectTemplatesService.getProjectTemplate.bind(this, projectTemplateId, null, options ? options.recordDescriptor : null), options,
      this.mapToReturn, !noPreProcess ? this.overrideMapToReturn : null, prefetchCallback).then(projectTemplate => {
        return projectTemplate;
      });
  }

  /**
  * @param  {object <projectTemplate>} projectTemplate - denormalized project template to create
  * @param  {object <object>} options.recordDescriptor - used to format returned project template
  * 
  * @returns {object<Promise<projectTemplate>>} projectTemplate - denormalized project template
  */
  createProjectTemplate(projectTemplate, options) {
    return projectTemplatesService.createProjectTemplate(projectTemplate, null, options).then(response => {
      if (response.error) {
        dataModel.displayHTTPError('Creating a project template', response.error, false);
        this.throwItemError('Error creating a project template', response);
      }
      else {
        this.normalizeAndSyncToStore(response);
        const denormalizedTemplate = this.mapToReturn(this.findItem(response.data.id), options);
        return denormalizedTemplate;
      }
    }).catch(error => {
      throw error;
    });
  }

  copyProjectTemplate(project_type_name, project_template_name, selectedMarkets, projectTemplateToCopyFrom, companyToCopyTo, options) {
    // First create the new projectType
    const project_type = { name: project_type_name, company_id: companyToCopyTo ? companyToCopyTo.id : localStorage.current_company_id };
    return dataModel.projectTypesDAO.createProjectType(project_type).then(projectType => {
      if (projectType) {
        return projectTemplatesService.copyProjectTemplate(projectType.id, projectTemplateToCopyFrom.id, project_template_name, selectedMarkets,
          companyToCopyTo && companyToCopyTo.id != localStorage.current_company_id ? companyToCopyTo.id : null, null, options).then(response => {
            if (response.error) {
              dataModel.displayHTTPError('Creating a project template', response.error, false);
              this.throwItemError('Error creating a project template', response);
            }
            else {
              this.normalizeAndSyncToStore(response);
              const denormalizedTemplate = this.mapToReturn(this.findItem(response.data.id), options);
              return denormalizedTemplate;
            }
          }).catch(error => {
            throw error;
          });
      }
    });
  }

  /**
  * @param  {number} projectTemplateId - ID of the latest template version
  * @param  {object <object>} options.recordDescriptor - used to format returned project template
  * 
  * @returns {object<Promise<projectTemplate>>} projectTemplate - denormalized project template
  */
  createProjectTemplateVersion(projectTemplateId, options) {
    return projectTemplatesService.createProjectTemplateVersion(projectTemplateId, null, options).then(response => {
      if (response.status === 202) {
        // 202 means the server queued the copy: the row does not exist yet, so there is no id
        // to return. Hand back the group so the caller can wait for the version and then land
        // on it. No toast, deliberately -- creating a version is one action from the user's
        // point of view, and announcing a background job they did not ask for was noise.
        const body = response.data || {};
        return { background: true, group_identifier: body.group_identifier };
      }
      if (response.error || (response.data && response.data.error)) {
        const err = response.error || (response.data && response.data.error);
        dataModel.displayHTTPError('Creating a project template version', err, false);
        this.throwItemError('Error creating a project template version', response);
        return;
      }
      const apiBody = response.data || response;
      return this.getProjectTemplate(projectTemplateId, { ...options }).then(async oldVersion => {
        this.normalizeAndSyncToStore(apiBody);
        const newId = (apiBody.data && apiBody.data.id) != null ? apiBody.data.id : apiBody.id;
        const denormalizedTemplate = this.mapToReturn(this.findItem(newId), options);
        dataModel.projectTypesDAO.getProjectType(denormalizedTemplate.project_type_id, { recordDescriptor: project_type_root }).then(projectType => {
          if (projectType && projectType.project_templates && !projectType.project_templates.find(pt => pt.id == denormalizedTemplate.id)) {
            projectType.project_templates.push(denormalizedTemplate);
            StoreItemProcessing.normalizeItem('project_type', projectType, { store: true });
          }
        });
        return denormalizedTemplate;
      });
    }).catch(error => {
      throw error;
    });
  }

  /**
  * @param  {number} projectTemplateId - ID of the latest template to publish
  * @param  {object <object>} options.recordDescriptor - used to format returned project template
  * 
  * @returns {object<Promise<projectTemplate>>} projectTemplate - denormalized project template
  */
  publishTemplateVersion(projectTemplateId, options) {
    return projectTemplatesService.publishTemplateVersion(projectTemplateId, null, options).then(response => {
      if (response.error) {
        dataModel.displayHTTPError('Publishing a project template', response.error, false);
        this.throwItemError('Publishing creating a project template', response);
      }
      else {
        return this.getProjectTemplate(projectTemplateId, { ...options }).then(async oldVersion => {
          this.normalizeAndSyncToStore(response);
          const denormalizedTemplate = this.mapToReturn(this.findItem(response.data.id), options);
          dataModel.projectTypesDAO.getProjectType(denormalizedTemplate.project_type_id, { recordDescriptor: project_type_root }).then(projectType => {
            if (!projectType.project_templates.find(pt => { return pt.id == denormalizedTemplate.id; })){
              projectType.project_templates.push(denormalizedTemplate);
              StoreItemProcessing.normalizeItem('project_type', projectType, { store: true });
            }
          });
            // TODO:
            // dataModel.projectTypesDAO.markNeedsAttributeSync(denormalizedTemplate.project_type_id)
          return denormalizedTemplate;
        });
      }
    }).catch(error => {
      throw error;
    });
  }

  /**
   * 
   * @param {number} templateId - template id to publish
   * @param {array <number>} projectIds - projects to migrate to new template
   * @param {object} options 
   */
  migrateAndPublish(templateId, projectIds = [], options) {
    return projectTemplatesService.migrateAndPublish(templateId, projectIds, null, options).then(response => {
      if (response.error) {
        dataModel.displayHTTPError('Publishing a project template', response.error, false);
        this.throwItemError('Publishing creating a project template', response);
      }
      else {
        const responseData = response.data;
        const isBackgroundProcess = response.status == 202;
        const result = { isBackgroundProcess };
        if (!isBackgroundProcess) {
          result.migratedProjects = responseData.projects;
        }
        this.normalizeAndSyncToStore(responseData.project_template);
        const denormalizedTemplate = this.mapToReturn(this.findItem(responseData.project_template.data.id), options);
        result.updatedTemplate = denormalizedTemplate;
        return result;
      }
    }).catch(error => {
      throw error;
    });
  }

  /**
  * @param  {object <projectTemplate>} projectTemplate - denormalized project template to update
  * @param  {object <object>} options.recordDescriptor - used to format returned project template
  * 
  * @returns {object<Promise<projectTemplate>>} projectTemplate - denormalized project template
  */
  async updateProjectTemplate(projectTemplate, options) {
    const normalizedProjectTemplate = this.normalizeItem(projectTemplate);
    this.markPending(normalizedProjectTemplate);
    const denormalizedProjectTemplate = this.mapToReturn(normalizedProjectTemplate, options);

    if (dataModel.isOffline) {
      this.markDirty(normalizedProjectTemplate, true);
      this.OfflineQueue.addQueueItem('update', { projectTemplate: denormalizedProjectTemplate });
      this.throwItemError();
    }
    else {
      this.markPending(normalizedProjectTemplate);
      this.normalizeItem(projectTemplate, { store: true });
      dataModel.actionCableConnector.internalUpdate({ type: 'updateProjectTemplate', project_template_id: projectTemplate.id }, projectTemplate.id);

      return projectTemplatesService.updateProjectTemplate(denormalizedProjectTemplate, null, options).then(response => {
        if (response.error && !response.offline) {
          dataModel.displayHTTPError('Updating a project template', response.error, false);
          this.markError(denormalizedProjectTemplate);
          this.throwItemError();
        }
        else if (response.offline) {
          this.markDirty(normalizedProjectTemplate, true);
          this.OfflineQueue.addQueueItem(this.itemType, 'update', { projectTemplate: denormalizedProjectTemplate });
          this.throwItemError();
        }
        else {
          this.markDirty(normalizedProjectTemplate, false);
          this.normalizeAndSyncToStore(response, options);
          let updatedProjectTemplate = this.findItem(projectTemplate.id);
          if (updatedProjectTemplate) {
            // TODO: what is this supposed to do?
            // projectTemplatesService.migrateProjectProjectTemplate(updatedProjectTemplate, updatedProjectTemplate)
          }
          return this.mapToReturn(this.findItem(projectTemplate.id), options);
        }
      })
        .catch((error) => {
          throw error;
        });
    }
  }

  /**
  * @param  {number} projectTemplateId - project template record identifier
  * 
  * @returns {object<Promise<string>>} result - "offline", "error", "success"
  */
  deleteProjectTemplate(projectTemplateId, is_template) {
    let projectTemplate = this.findItem(projectTemplateId);

    // TODO: action cable
    if (dataModel.isOffline) {
      this.OfflineQueue.addQueueItem(this.itemType, 'delete', { projectTemplateId });
      return Promise.resolve('offline');
    }
    return projectTemplatesService.deleteProjectTemplate(projectTemplateId, is_template).then(response => {
      if (response.error && !response.offline) {
        if(is_template){
          if(response.error.response.data && response.error.response.data.message === 'Project template has projects'){
            dataModel.displayInfoErrorMessage('Cannot delete the template because its linked to an active project.');
          }
          else if(response.error.response.data && response.error.response.data.message === 'Project template not found') {
            this.unsetProjectTemplate(projectTemplateId);
            dataModel.displayInfoErrorMessage('The project template was successfully deleted.', 4000, 1);
          }
          else{
            dataModel.displayHTTPError('Deleting a project template', response.error, false);
          }
        }
        else{
          dataModel.displayHTTPError('Deleting a project template version', response.error, false);
        }

        StoreItemProcessing.setItemInStore(this.itemType, StoreItemProcessing.normalizeItem(projectTemplate));
        this.throwItemError();
      }
      else if (response.offline) {
        this.OfflineQueue.addQueueItem(this.itemType, 'delete', { projectTemplateId });
        this.throwItemError(); //make sure to send the denormalized build
        // return 'offline'
      }
      else {
        if (projectTemplate) {
          // find all project templates with the same group_identifier in store
          const state = dataModel.store.getState();
          const templateIdsToRemove = Object.values(state.new_project_templates).filter(project => project.group_identifier === projectTemplate.group_identifier);
          // loop trough all and delete them
          templateIdsToRemove.forEach(template => this.unsetProjectTemplate(template.id));
        }
        
        if(is_template){
          dataModel.displayInfoErrorMessage('The project template was successfully deleted.', 4000, 1);
        }
        else{
          dataModel.displayInfoErrorMessage('The project template version was successfully deleted.', 4000, 1);
        }
        return this.mapToReturn(projectTemplate);
      }
    });
  }

  /**
  * @param  {number} projectTemplateId - project template record identifier
  * 
  * @returns {object<Promise<string>>} result - "offline", "error", "success"
  */
  archive(projectTemplateId) {
    let projectTemplate = this.findItem(projectTemplateId);
    if (projectTemplate) {
      this.unsetProjectTemplate(projectTemplateId);
    }

    // TODO: action cable
    if (dataModel.isOffline) {
      this.markDirty(projectTemplate, true);
      this.OfflineQueue.addQueueItem(this.itemType, 'archive', { projectTemplateId });
      return Promise.resolve('offline');
    }
    return projectTemplatesService.archiveProjectTemplate(projectTemplateId).then(response => {
      if (response.error && !response.offline) {
        dataModel.displayHTTPError('Archiving a project template', response.error, false);

        StoreItemProcessing.setItemInStore(this.itemType, StoreItemProcessing.normalizeItem(projectTemplate));
        this.throwItemError();
      }
      else if (response.offline) {
        this.markDirty(projectTemplate, true);
        this.OfflineQueue.addQueueItem(this.itemType, 'archive', { projectTemplateId });
        this.throwItemError();
      }
      else {
        this.markDirty(projectTemplate, false);
        dataModel.displayInfoErrorMessage('The project template was successfully archived.', 4000, 1);
        return this.mapToReturn(projectTemplate);
      }
    });  
  }

  addReferenceDocument(docId, projectTemplateId, eventMetadatum, options) {
    const projectTemplate = this.findItem(projectTemplateId);
    if (!projectTemplate) {
      this.throwItemError();
    }

    if (!projectTemplate.reference_documents) {
      projectTemplate.reference_documents = [];
    }

    if (!eventMetadatum) {
      eventMetadatum = Metadata.generateMetadatum();
    }

    projectTemplate.reference_documents.push(docId);
    this.setItemInStore(projectTemplate);

    if (dataModel.isOffline) {
      this.markDirty(projectTemplate, true);
      this.OfflineQueue.addQueueItem('projectTemplate', 'add_doc', { docId, projectTemplateId, eventMetadatum });
      this.throwItemError('adding a reference doc - offline', projectTemplate); 
      // return Promise.resolve(projectTemplate)
    }
    else {
      return projectTemplatesService.addReferenceDocument(projectTemplateId, docId, eventMetadatum).then(response => {
        if (response.error && !response.offline) {
          dataModel.displayHTTPError('adding a reference document', response.error, false);
          StoreItemProcessing.setItemInStore(this.itemType, StoreItemProcessing.normalizeItem(projectTemplate));
        }
        else if (response.offline) {
          this.markDirty(projectTemplate, true);
          this.OfflineQueue.addQueueItem('projectTemplate', 'add_doc', { docId, projectTemplateId, eventMetadatum });
          this.throwItemError('adding a reference doc - offline', projectTemplate); 
        }
        else {
          this.markDirty(projectTemplate, false);
          return this.mapToReturn(response.data);
        }
      })
      .catch((error) => {
        this.markError(projectTemplate);
        this.throwItemError(error.message, projectTemplate); 
      });
    }
  }

  removeReferenceDocument(docId, projectTemplateId, eventMetadatum, options) {
    const projectTemplate = this.findItem(projectTemplateId);
    if (!projectTemplate) {
      this.throwItemError();
    }

    if (projectTemplate.reference_documents) {
      projectTemplate.reference_documents = projectTemplate.reference_documents.filter(refDocId => {
        return refDocId != docId;
      });
    }

    if (!eventMetadatum) {
      eventMetadatum = Metadata.generateMetadatum();
    }

    this.setItemInStore(projectTemplate);

    if (dataModel.isOffline) {
      this.markDirty(projectTemplate, true);
      this.OfflineQueue.addQueueItem('projectTemplate', 'remove_doc', { docId, projectTemplateId, eventMetadatum });
      this.throwItemError(); 
    }
    else {
      return projectTemplatesService.deleteReferenceDocument(projectTemplateId, docId, eventMetadatum).then(response => {
        if (response.error && !response.offline) {
          dataModel.displayHTTPError('removing a reference document', response.error, false);
          StoreItemProcessing.setItemInStore(this.itemType, StoreItemProcessing.normalizeItem(projectTemplate));
        }
        else if (response.offline) {
          this.markDirty(projectTemplate, true);
          this.OfflineQueue.addQueueItem('projectTemplate', 'remove_doc', { docId, projectTemplateId, eventMetadatum });
          this.throwItemError('removing a reference doc - offline', projectTemplate); 
        }
        else {
          this.markDirty(projectTemplate, false);
          return this.mapToReturn(response.data);
        }
      })
      .catch((error) => {
        this.markError(projectTemplate);
        this.throwItemError(error.message, projectTemplate);
      });
    }
  }

  /** Util Functions */
  completeOfflineUpdate(queueItem) {
    switch (queueItem.operationKey) {
    case 'create':
      return this.createProjectTemplate(queueItem.itemInfo.projectTemplate);
    case 'update':
      return this.updateProjectTemplate(queueItem.itemInfo.projectTemplate);
    case 'delete':
      return this.deleteProjectTemplate(queueItem.itemInfo.projectTemplateId);
    case 'archive':
      return this.archive(queueItem.itemInfo.projectTemplateId);
    case 'add_doc':
      return this.addReferenceDocument(queueItem.itemInfo.docId, queueItem.itemInfo.projectTemplateId, queueItem.itemInfo.eventMetadatum);
    case 'remove_doc':
      return this.removeReferenceDocument(queueItem.itemInfo.docId, queueItem.itemInfo.projectTemplateId, queueItem.itemInfo.eventMetadatum);
      
    default:
      break;
    }
  }

  handleActionCableAndGetRequest(acData) {    
    // TODO: could be good to have our own findItem that calls StoreItemProcessing
    let projectTemplate = StoreItemProcessing.findItem(this.itemType, acData.project_template_id);
    if (projectTemplate) {
      if (acData.is_attribute_update) {
        this.markNeedsAttributeSync(projectTemplate);
      }
      else {
        this.markNeedsSync(projectTemplate);
      }
    }
    switch (acData.type) {
    case 'newProjectTemplate':
        // 202 flow: server creates template in background; we GET by project_template_id
      const newTemplateId = acData.project_template_id;
      console.log('🚀 ~ ProjectTemplatesDAO ~ handleActionCableAndGetRequest ~ newTemplateId:', newTemplateId);
      return (recordId, options) => this.getProjectTemplate(newTemplateId, options);
    case 'updateProjectTemplate':
      const updateTemplateId = acData.project_template_id;
      return (recordId, options) => this.getProjectTemplate(updateTemplateId, options);
    case 'deleteProjectTemplate':
      const deletedTemplate = StoreItemProcessing.deleteItemInStore(this.itemType, acData.project_template_id);
      return Promise.resolve(deletedTemplate);
    default:
    }
  }

  clearState(options) {
    this.clearMyState(this.stateTree.new_project_templates, options.toIgnore.project_templates, this.unsetProject, this.clearProjects, options);
  }
}



// WEBPACK FOOTER //
// ./src/dao/ProjectTemplatesDAO.js